import React, { useState } from 'react'
import Button from '@mui/material/Button'
import Loader from '../../../components/Loader'
import TaskModal from '../../../components/Modal'
import useCreateHelp from '../services/hooks/useCreateHelp'
import useDeleteHelp from '../services/hooks/useHelpDelete'
import useHelpPoint from '../services/hooks/useHelpPoint'
import useValidation from '../../../hooks/useValidation'
import useUpdateHelp from '../services/hooks/useUpdateHelp'
import cacheKeys from '../../../services/cacheKeys'
import { invalidateQuery } from '../../../services/config/queryClient'
import { HelpItem } from '../services/types/APIResponseType'
import HelpModalContent from './component/HelpModalContent'
import HelpAccordion from './component/HelpAccordion'

const Help = () => {
  const inputValidationRules = {
    question: [{ required: true, message: 'Please enter your question.' }],
    answer: [{ required: true, message: 'Please enter your answer.' }],
  }

  const [open, setOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [formValues, setFormValues] = useState({
    question: '',
    answer: '',
  })
  const [isDeleteModal, setIsDeleteModal] = useState(false)
  const [helpId, setHelpId] = useState<string | null>(null)
  const [isEdit, setIsEdit] = useState(false)
  const { errors, validateInputs } = useValidation(
    {
      question: formValues.question,
      answer: formValues.answer,
    },
    inputValidationRules,
  )

  const { data: helpPoint, isLoading } = useHelpPoint()
  const { mutate: createHelp, isPending } = useCreateHelp()
  const { mutate: updateHelp, isPending: updateHelpPending } = useUpdateHelp()
  const { mutate: deleteHelp } = useDeleteHelp()

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setErrorMsg('')
    setFormValues({
      question: '',
      answer: '',
    })
    setHelpId(null)
    setTimeout(() => {
      setIsEdit(false)
    }, 200)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }))
  }

  const handleSubmit = () => {
    if (!validateInputs()) {
      return
    }
    if (isEdit && helpId) {
      updateHelp(
        { helpId, ...formValues },
        {
          onSuccess: () => {
            setOpen(false)
            setIsEdit(false)
            setHelpId(null)
            invalidateQuery([cacheKeys.HELP_POINT])
          },
          onError: (error) => {
            setErrorMsg(
              error.message || 'Something went wrong. Please try again.',
            )
          },
        },
      )
    } else {
      createHelp(formValues, {
        onSuccess: () => {
          setFormValues({
            question: '',
            answer: '',
          })
          setOpen(false)
          invalidateQuery([cacheKeys.HELP_POINT])
        },
        onError: (error) => {
          setErrorMsg(
            error.message || 'Something went wrong. Please try again.',
          )
        },
      })
    }
  }

  const isFormValid = () => {
    return Object.values(formValues).every((value) => value.trim() !== '')
  }

  const handleEditHelp = (item: HelpItem) => {
    setIsEdit(true)
    setOpen(true)
    setHelpId(item._id)
    setFormValues({
      question: item.question,
      answer: item.answer,
    })
  }

  const handleDeleteHelp = (item: string) => {
    setIsDeleteModal(true)
    deleteHelp(
      {
        helpId: item,
      },
      {
        onSuccess: () => {
          setIsDeleteModal(false)
          invalidateQuery([cacheKeys.HELP_POINT])
        },
      },
    )
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <div className="flex justify-end mb-3 mr-2">
        <Button onClick={handleOpen}>
          <img alt="Add icon" src="/icon/plus-icon.svg" />
        </Button>

        <TaskModal
          content={
            <HelpModalContent
              open={open}
              isEdit={isEdit}
              formValues={formValues}
              errorMsg={errorMsg}
              isPending={isPending}
              updateHelpPending={updateHelpPending}
              handleClose={handleClose}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              isFormValid={isFormValid}
              errors={errors}
            />
          }
          open={open}
          handleClose={handleClose}
        />
      </div>
      <div className="my-2">
        {helpPoint?.data?.help.map((item: HelpItem) => (
          <>
            <HelpAccordion
              key={item._id}
              item={item}
              onEdit={handleEditHelp}
              onDelete={handleDeleteHelp}
              isDeleteModal={isDeleteModal}
              setIsDeleteModal={setIsDeleteModal}
              handleClose={handleClose}
            />
            <div className="w-full h-[1px] bottom-0 bg-border-gradient"></div>
          </>
        ))}
      </div>
    </>
  )
}

export default Help
